import { useMutation, useQueryClient } from 'react-query';
import { useToast } from '@razorpay/blade/components';
import attendance from 'api/attendance';
import type { UpdateAttendanceSettingsRequest } from 'api/types/attendanceSettingsTypes';
import { buildRequest } from '../utils';
import { ATTENDANCE_SETTINGS_QUERY_KEY } from './useAttendanceSettings';
import { AttendanceSettingsFormState } from 'components/Settings/AttendanceAndLeavesSettings/types';
import { AppError } from 'utils/AppError';
import { parseError } from 'utils/Api';

export function useToggleAttendance({
  onSuccess,
}: {
  onSuccess?: (data: { isAttendanceEnabled: boolean }) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      existingFormState,
      enabled,
    }: {
      existingFormState: Partial<AttendanceSettingsFormState>;
      enabled: boolean;
    }) => {
      const request: UpdateAttendanceSettingsRequest = buildRequest({
        ...existingFormState,
        isAttendanceEnabled: enabled,
      });
      return attendance.updateAttendanceSettings(request);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([ATTENDANCE_SETTINGS_QUERY_KEY]);
      toast.show({
        content: variables.enabled ? 'Time & Attendance enabled.' : 'Time & Attendance disabled.',
        color: variables.enabled ? 'positive' : 'notice',
      });
      onSuccess?.({
        isAttendanceEnabled: variables.enabled,
      });
    },
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseError(error),
        color: 'negative',
      });
    },
  });
}
